/**
 * ContractPreview — A4-style HTML preview of a contract
 * Full contract text + employer signing flow
 */

import { useState } from 'react';
import type { Contract } from '../../stores/workforce-store';
import { useWorkforceStore } from '../../stores';
import { SignatureCanvas } from './SignatureCanvas';

function formatDateNL(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  const months = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(amount);
}

// Uren netjes weergeven: hele getallen zonder decimalen, anders NL-komma (104,0 -> 104; 197,6 -> 197,6)
function formatUren(n: number | null | undefined): string {
  if (n === null || n === undefined) return '-';
  return n % 1 === 0 ? String(n) : n.toFixed(1).replace('.', ',');
}

const statusStyles: Record<string, string> = {
  concept: 'bg-gray-100 text-gray-700',
  werkgever_getekend: 'bg-blue-100 text-blue-700',
  verstuurd: 'bg-amber-100 text-amber-700',
  getekend: 'bg-emerald-100 text-emerald-700',
  verlopen: 'bg-red-100 text-red-700',
  'beëindigd': 'bg-gray-100 text-gray-500',
};

const statusLabels: Record<string, string> = {
  concept: 'concept',
  werkgever_getekend: 'werkgever getekend',
  verstuurd: 'verstuurd',
  getekend: 'getekend',
  verlopen: 'verlopen',
  'beëindigd': 'beëindigd',
};

interface ContractPreviewProps {
  contract: Contract;
  onBack: () => void;
  onEdit?: () => void;
  onSent?: (updated: Contract) => void;
  onEmployerSign?: (updated: Contract) => void;
}

export function ContractPreview({ contract, onBack, onEdit, onSent, onEmployerSign }: ContractPreviewProps) {
  const { sendContract, downloadContractPdf, contracts } = useWorkforceStore();
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [showSignCanvas, setShowSignCanvas] = useState(false);
  const [employerSigData, setEmployerSigData] = useState<string | null>(null);
  const c = contract;
  const isRegular = c.employment_type === 'regulier';
  const isAllin = c.employment_type === '0-uren-allin';
  const isMinMax = c.employment_type === 'min-max';
  const isJaaruren = c.employment_type === 'jaaruren';
  // Vast salaris: vast bruto maandsalaris zonder urenregistratie, zelfstandig geformuleerd (geen cao-incorporatie)
  const isVastSalaris = c.employment_type === 'vast-salaris';
  const isBepaald = c.contract_type === 'bepaald';
  // Min/max, jaaruren en vast-salaris gedragen zich als vast dienstverband (werknemer-taal, opzegtermijn, pensioen)
  const isVastVerband = isRegular || isMinMax || isJaaruren || isVastSalaris;
  // Vast salaris: vakantiedagen afgeleid uit de uren (dag = uren_per_week / 5)
  const vakantieDagen = c.uren_per_week && c.vakantieuren_per_jaar
    ? Math.round(c.vakantieuren_per_jaar / (c.uren_per_week / 5))
    : null;
  // Jaaruren: norm en vast maandsalaris (1/12 van jaarurennorm x uurloon)
  const jaarNorm = c.jaaruren_norm ?? (c.uren_per_week ? Math.round(c.uren_per_week * 52) : 0);
  const maandSalaris = jaarNorm * c.bruto_uurloon / 12;
  // Contractwissel: zittende werknemer, artikel 1 wordt een vervangingsclausule met behoud van anciënniteit
  const isWissel = !!c.oorspronkelijke_datum_in_dienst && c.oorspronkelijke_datum_in_dienst !== c.start_date;
  // Voortzetting van rechtswege (art. 7:668 lid 4): geen nieuwe afspraak, geen handtekeningen
  const isStilzwijgend = !!c.stilzwijgend_verlengd;
  const vorigNummer = contracts.find(x => x.id === c.previous_contract_id)?.contract_number ?? 'het vorige contract';
  // Bepaalde tijd eindigt van rechtswege; zonder expliciet beding kan geen van beide partijen tussentijds
  // opzeggen (art. 7:667 lid 3 BW). Geldt voor elk bepaalde-tijdcontract, niet alleen oproep.
  const tussentijdsOpzegbeding = `Deze arbeidsovereenkomst kan door beide partijen tussentijds worden opgezegd met inachtneming van ${isVastVerband ? 'de in artikel 9 bepaalde opzegtermijn' : 'de wettelijke opzegtermijn'}. Opzegging dient schriftelijk te geschieden tegen het einde van een kalendermaand.`;

  const title = (isRegular || isVastSalaris)
    ? `Arbeidsovereenkomst voor ${isBepaald ? 'bepaalde' : 'onbepaalde'} tijd`
    : isJaaruren
    ? `Arbeidsovereenkomst met jaarurennorm (${isBepaald ? 'bepaalde' : 'onbepaalde'} tijd)`
    : isMinMax
    ? `Arbeidsovereenkomst met minimum- en maximumuren (${isBepaald ? 'bepaalde' : 'onbepaalde'} tijd)`
    : `Arbeidsovereenkomst met uitgestelde prestatieplicht voor oproepkracht (${isBepaald ? 'bepaalde' : 'onbepaalde'} tijd)`;

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="text-sm text-indigo-600 hover:underline">
          &larr; Terug naar contracten
        </button>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusStyles[c.status] || 'bg-gray-100'}`}>
            {statusLabels[c.status] || c.status}
          </span>
          {c.status === 'concept' && onEdit && (
            <button
              onClick={onEdit}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
            >
              Bewerken
            </button>
          )}
          {c.status === 'concept' && (
            <button
              onClick={() => setShowSignCanvas(!showSignCanvas)}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors"
            >
              Onderteken als werkgever
            </button>
          )}
          {c.status === 'werkgever_getekend' && (
            <button
              disabled={isSending}
              onClick={async () => {
                setIsSending(true);
                setSendError(null);
                const err = await sendContract(c.id);
                setIsSending(false);
                if (!err) {
                  onSent?.({ ...c, status: 'verstuurd' });
                } else {
                  setSendError(err);
                }
              }}
              className="px-4 py-2 bg-indigo-500 text-white rounded-lg text-sm font-medium hover:bg-indigo-600 disabled:opacity-50 transition-colors"
            >
              {isSending ? 'Versturen...' : 'Verstuur ter ondertekening'}
            </button>
          )}
          <button
            onClick={() => downloadContractPdf(c.id)}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
          >
            Download PDF
          </button>
        </div>
      </div>

      {/* Employer sign canvas */}
      {showSignCanvas && c.status === 'concept' && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl space-y-3">
          <h3 className="text-sm font-semibold text-blue-900">Werkgever-handtekening</h3>
          <SignatureCanvas
            onSave={setEmployerSigData}
            onClear={() => setEmployerSigData(null)}
          />
          <button
            disabled={!employerSigData}
            onClick={() => onEmployerSign && employerSigData && onEmployerSign({ ...c, employer_signature_data: employerSigData })}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            Bevestig handtekening
          </button>
        </div>
      )}

      {/* Send error */}
      {sendError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {sendError}
        </div>
      )}

      {/* Internal notes */}
      {c.notes && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm">
          <span className="font-medium text-amber-800">Interne notitie:</span>{' '}
          <span className="text-amber-700">{c.notes}</span>
        </div>
      )}

      {/* A4-style preview */}
      <div className="bg-white shadow-lg rounded-xl border border-gray-200 max-w-3xl mx-auto p-12 min-h-[800px]">
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <img src="/cinco-logo.png" alt="Cinco Cleaning" className="h-7" />
          </div>
          <div className="text-right text-xs text-gray-500">
            <p>Cinco Cleaning B.V.</p>
            <p>Havelte, Nederland</p>
            <p>KvK: 04061759</p>
          </div>
        </div>

        {/* Title */}
        <h1 className={`text-center text-xl font-bold text-gray-900 ${isStilzwijgend ? 'mb-3' : 'mb-8'}`}>{title}</h1>
        {isStilzwijgend && (
          <p className="text-center text-sm text-violet-700 mb-8">
            Voortzetting van rechtswege (art. 7:668 lid 4 BW) van {vorigNummer}, van {formatDateNL(c.start_date)}
            {c.end_date ? ` tot en met ${formatDateNL(c.end_date)}` : ' voor onbepaalde tijd'}, onder dezelfde voorwaarden.
          </p>
        )}

        {/* Partijen */}
        <div className="mb-8 text-sm leading-relaxed">
          <p className="font-bold mb-2">De ondergetekenden:</p>
          <p className="mb-2">
            1. Cinco Cleaning B.V., gevestigd te Havelte, rechtsgeldig vertegenwoordigd door {c.bedrijf_vertegenwoordigd_door}, hierna te noemen "werkgever";
          </p>
          <p className="mb-2">
            2. {c.aanhef_werknemer} {c.voorletters} {c.achternaam}, geboren op {c.geboortedatum ? formatDateNL(c.geboortedatum) : '[geboortedatum]'}, wonende te {c.woonplaats || '[woonplaats]'}, hierna te noemen "werknemer";
          </p>
          <p>zijn overeengekomen als volgt:</p>
        </div>

        {/* Articles — FULL TEXT */}
        <div className="space-y-5 text-sm leading-relaxed">
          {/* Art 1 — Indiensttreding, of vervangingsclausule bij contractwissel */}
          {isWissel ? (
            <div>
              <h3 className="font-bold text-gray-900 mb-1">Artikel 1 — Indiensttreding en voortzetting dienstverband</h3>
              <p>1.1. Werknemer is op {formatDateNL(c.oorspronkelijke_datum_in_dienst!)} bij werkgever in dienst getreden. Deze arbeidsovereenkomst vervangt met ingang van {formatDateNL(c.start_date)} de tussen partijen bestaande arbeidsovereenkomst en wordt aangegaan voor {isBepaald ? 'bepaalde' : 'onbepaalde'} tijd.</p>
              <p>1.2. Het dienstverband wordt geacht onafgebroken te hebben voortgeduurd sinds {formatDateNL(c.oorspronkelijke_datum_in_dienst!)}. Voor alle regelingen waarbij de duur van het dienstverband van belang is, waaronder de opzegtermijn, loondoorbetaling bij ziekte en de transitievergoeding, geldt die datum als datum van indiensttreding.</p>
              {isBepaald && c.end_date && (
                <>
                  <p>1.3. De arbeidsovereenkomst eindigt van rechtswege op {formatDateNL(c.end_date)}, zonder dat daartoe enige opzegging is vereist.</p>
                  <p>1.4. {tussentijdsOpzegbeding}</p>
                </>
              )}
            </div>
          ) : (
            <div>
              <h3 className="font-bold text-gray-900 mb-1">Artikel 1 — Indiensttreding</h3>
              <p>1.1. Werknemer treedt met ingang van {formatDateNL(c.start_date)} voor {isBepaald ? 'bepaalde' : 'onbepaalde'} tijd in dienst bij werkgever.</p>
              {isBepaald && c.end_date && (
                <>
                  <p>1.2. De arbeidsovereenkomst eindigt van rechtswege op {formatDateNL(c.end_date)}, zonder dat daartoe enige opzegging is vereist.</p>
                  <p>1.3. {tussentijdsOpzegbeding}</p>
                </>
              )}
            </div>
          )}

          {/* Art 2 — altijd aanwezig zodat de artikelnummering doorloopt */}
          <div>
            <h3 className="font-bold text-gray-900 mb-1">Artikel 2 — Proeftijd</h3>
            {c.proeftijd_maanden > 0 ? (
              <p>2.1. De eerste {c.proeftijd_maanden === 1 ? 'maand geldt' : `${c.proeftijd_maanden} maanden gelden`} als proeftijd. Gedurende de proeftijd kan de arbeidsovereenkomst door beide partijen met onmiddellijke ingang worden beëindigd.</p>
            ) : (
              <p>2.1. Er geldt geen proeftijd.</p>
            )}
          </div>

          {/* Art 3 */}
          <div>
            <h3 className="font-bold text-gray-900 mb-1">Artikel 3 — Functie</h3>
            <p>3.1. De {isVastVerband ? 'werknemer' : 'oproepkracht'} wordt aangesteld in de functie van {c.functie}.</p>
            <p>3.2. De {isVastVerband ? 'werknemer' : 'oproepkracht'} verplicht zich de hem opgedragen werkzaamheden naar beste vermogen te verrichten{isVastVerband ? ' en zich te houden aan de binnen het bedrijf geldende regels, voorschriften en aanwijzingen' : ''}.</p>
            {isVastVerband ? (
              <p>3.3. De werkgever behoudt zich het recht voor de werknemer ook andere werkzaamheden op te dragen die redelijkerwijs van de werknemer kunnen worden verwacht.</p>
            ) : (
              <p>3.3. De oproepkracht is niet verplicht gehoor te geven aan een oproep van de werkgever. Er is sprake van een arbeidsovereenkomst met uitgestelde prestatieplicht.</p>
            )}
            <p>3.4. De werkzaamheden worden verricht vanuit de vestiging van werkgever te Havelte en op wisselende locaties binnen Nederland.</p>
          </div>

          {/* Art 4 */}
          <div>
            <h3 className="font-bold text-gray-900 mb-1">Artikel 4 — {isVastVerband ? 'Arbeidsduur en beloning' : 'Beloning'}</h3>
            {isVastSalaris ? (
              <>
                <p>4.1. De arbeidsduur bedraagt gemiddeld {formatUren(c.uren_per_week)} uur per week. Gelet op de aard van de functie registreert de werknemer geen uren; de werknemer richt zijn werktijden in overleg met de werkgever zo in als voor een goede vervulling van de functie nodig is.</p>
                <p>4.2. Het salaris bedraagt {formatCurrency(c.bruto_maandsalaris ?? 0)} bruto per maand. Het salaris wordt maandelijks achteraf betaald, uiterlijk op de laatste werkdag van de maand.</p>
                <p>4.3. In het salaris is een vergoeding voor eventueel meerwerk begrepen. Structureel overwerk dat redelijkerwijs niet meer bij de functie past, wordt in overleg tussen partijen besproken.</p>
                <p>4.4. De werknemer ontvangt een vakantietoeslag van {c.vakantietoeslag_percentage}% per jaar over het bruto jaarsalaris. Uitbetaling vindt plaats in de maand mei.</p>
              </>
            ) : isRegular ? (
              <>
                <p>4.1. De arbeidsduur bedraagt {c.uren_per_week} uur per week.</p>
                <p>4.2. Het bruto uurloon bedraagt {formatCurrency(c.bruto_uurloon)}.</p>
                <p>4.3. Het salaris wordt maandelijks achteraf betaald, uiterlijk op de laatste werkdag van de maand.</p>
                <p>4.4. De werknemer ontvangt een vakantietoeslag van {c.vakantietoeslag_percentage}% per jaar over het bruto jaarsalaris. Uitbetaling vindt plaats in de maand mei.</p>
              </>
            ) : isJaaruren ? (
              <>
                <p>4.1. De arbeidsduur bedraagt gemiddeld {c.uren_per_week} uur per week, oftewel {formatUren(jaarNorm)} uur per kalenderjaar (de jaarurennorm). De feitelijke arbeidsduur kan per week wisselen met het werkaanbod en wordt vastgelegd in het rooster, binnen de kaders van de cao en de Arbeidstijdenwet.</p>
                <p>4.2. Het bruto uurloon bedraagt {formatCurrency(c.bruto_uurloon)}. De werknemer ontvangt een vast brutosalaris van {formatCurrency(maandSalaris)} per maand (1/12 van de jaarurennorm maal het bruto uurloon), ongeacht het aantal in die maand gewerkte uren. Het salaris wordt maandelijks achteraf betaald, uiterlijk op de laatste werkdag van de maand.</p>
                <p>4.3. Het verschil tussen de gewerkte uren en de naar rato verstreken jaarurennorm wordt bijgehouden als spaarurensaldo, overeenkomstig het spaarurensysteem van artikel 8 van het B-deel van de cao Schoonmaak- en Glazenwassersbedrijf. De referteperiode loopt van januari tot en met december. Opgenomen vakantie-uren en uren van doorbetaalde ziekte en feestdagen gelden hierbij als gewerkte uren.</p>
                <p>4.4. Het spaarurensaldo bedraagt ten hoogste 100 uur; bij een deeltijddienstverband geldt dit maximum naar rato. Uren waarmee het spaarurensaldo dit maximum zou overschrijden, worden bij de eerstvolgende maandelijkse loonbetaling uitbetaald tegen het bruto uurloon. Heeft de werknemer aan het einde van de referteperiode minder uren gewerkt dan de jaarurennorm, dan komen deze minuren voor rekening van de werkgever en vervallen zij. Niet-ingezette plusuren worden na afloop van de referteperiode uitbetaald tegen het bruto uurloon, met vakantietoeslag en vakantieopbouw over deze uren. Voor uren boven de jaarurennorm gelden de bepalingen van de cao.</p>
                <p>4.5. De werknemer kan het actuele spaarurensaldo op elk moment inzien via het medewerkerportaal van Cinco Cleaning en registreert daar ook de gewerkte uren.</p>
                <p>4.6. De werknemer ontvangt een vakantietoeslag van {c.vakantietoeslag_percentage}% per jaar over het bruto jaarsalaris. Uitbetaling vindt plaats in de maand mei.</p>
                <p>4.7. Vangt deze overeenkomst in de loop van de referteperiode aan of eindigt het dienstverband in de loop daarvan, dan geldt de jaarurennorm naar rato van de periode waarin deze overeenkomst in dat kalenderjaar van kracht is. Bij het einde van het dienstverband wordt het spaarurensaldo afgerekend: plusuren worden uitbetaald, minuren komen voor rekening van de werkgever.</p>
              </>
            ) : isMinMax ? (
              <>
                <p>4.1. De arbeidsduur bedraagt ten minste {c.uren_min} uur en ten hoogste {c.uren_max} uur per week. De minimumuren gelden als garantie-uren: het loon over deze uren wordt ook betaald wanneer er minder werk voorhanden is.</p>
                <p>4.2. Voor de uren boven de garantie-uren geldt een oproep. De werkgever roept de werknemer ten minste vier dagen van tevoren op. Trekt de werkgever een oproep binnen die termijn in, dan behoudt de werknemer het recht op loon over de opgeroepen uren.</p>
                <p>4.3. Het bruto uurloon bedraagt {formatCurrency(c.bruto_uurloon)}.</p>
                <p>4.4. Het salaris wordt maandelijks achteraf betaald op basis van de garantie-uren en de daarboven daadwerkelijk gewerkte uren, uiterlijk op de laatste werkdag van de maand.</p>
                <p>4.5. De werknemer registreert de gewerkte uren via het medewerkerportaal van Cinco Cleaning.</p>
                <p>4.6. De werknemer ontvangt een vakantietoeslag van {c.vakantietoeslag_percentage}% per jaar over het bruto jaarsalaris. Uitbetaling vindt plaats in de maand mei.</p>
                <p>4.7. Telkens wanneer de arbeidsovereenkomst twaalf maanden heeft geduurd, doet de werkgever de werknemer een aanbod voor een vaste arbeidsomvang op basis van de gemiddelde arbeidsomvang in de voorafgaande twaalf maanden (artikel 7:628a BW).</p>
              </>
            ) : isAllin ? (
              <>
                <p>4.1. Het all-in bruto uurloon bedraagt {formatCurrency(c.bruto_uurloon)}. In dit bedrag zijn de vakantietoeslag en vakantie-uren verwerkt.</p>
                <p>4.2. Het salaris wordt maandelijks achteraf betaald op basis van de daadwerkelijk gewerkte uren, uiterlijk op de laatste werkdag van de maand.</p>
                <p>4.3. De oproepkracht registreert de gewerkte uren via het medewerkerportaal van Cinco Cleaning.</p>
                <p>4.4. De vakantietoeslag van {c.vakantietoeslag_percentage}% is verwerkt in het bruto uurloon.</p>
              </>
            ) : (
              <>
                <p>4.1. Het bruto uurloon bedraagt {formatCurrency(c.bruto_uurloon)}.</p>
                <p>4.2. Het salaris wordt maandelijks achteraf betaald op basis van de daadwerkelijk gewerkte uren, uiterlijk op de laatste werkdag van de maand.</p>
                <p>4.3. De oproepkracht registreert de gewerkte uren via het medewerkerportaal van Cinco Cleaning.</p>
                <p>4.4. De werknemer ontvangt een vakantietoeslag van {c.vakantietoeslag_percentage}% per jaar over het hem toekomende brutosalaris. Uitbetaling vindt plaats in de maand mei.</p>
              </>
            )}
          </div>

          {/* Art 4a — Meerwerk (alleen regulier) */}
          {isRegular && (
            <div>
              <h3 className="font-bold text-gray-900 mb-1">Artikel 4a — Meerwerk</h3>
              <p>4a.1. In overleg kan de werknemer meer uren werken dan de overeengekomen arbeidsduur. Meeruren worden uitbetaald tegen het bruto uurloon; vakantietoeslag en vakantie worden ook over de meeruren opgebouwd.</p>
              <p>4a.2. De werknemer registreert de gewerkte uren via het medewerkerportaal van Cinco Cleaning.</p>
            </div>
          )}

          {/* Art 5 */}
          <div>
            <h3 className="font-bold text-gray-900 mb-1">Artikel 5 — Vakantie</h3>
            {isVastSalaris ? (
              <>
                <p>5.1. De werknemer heeft recht op {vakantieDagen ?? '—'} vakantiedagen ({formatUren(c.vakantieuren_per_jaar)} vakantie-uren) per jaar op basis van de overeengekomen arbeidsduur.</p>
                <p>5.2. Vakantie wordt in overleg met de werkgever opgenomen. De werkgever behoudt zich het recht voor vakantieaanvragen te weigeren bij zwaarwegende bedrijfsbelangen.</p>
                <p>5.3. Vakantieaanvragen worden ingediend via het medewerkerportaal.</p>
              </>
            ) : (isRegular || isJaaruren) ? (
              <>
                <p>5.1. De werknemer heeft recht op {formatUren(c.vakantieuren_per_jaar)} vakantie-uren per jaar, gebaseerd op 26 vakantiedagen naar rato van de arbeidsduur.</p>
                <p>5.2. Per week bouwt de werknemer {formatUren(c.vakantie_opbouw_per_week)} vakantie-uren op.</p>
                <p>5.3. Vakantie wordt in overleg met de werkgever opgenomen. De werkgever behoudt zich het recht voor vakantieaanvragen te weigeren bij zwaarwegende bedrijfsbelangen.</p>
                <p>5.4. Vakantieaanvragen worden ingediend via het medewerkerportaal.</p>
              </>
            ) : isMinMax ? (
              <>
                <p>5.1. De werknemer bouwt {c.vakantie_percentage}% vakantie op over alle uitbetaalde uren.</p>
                <p>5.2. Vakantie wordt in overleg met de werkgever opgenomen. De werkgever behoudt zich het recht voor vakantieaanvragen te weigeren bij zwaarwegende bedrijfsbelangen.</p>
                <p>5.3. Vakantieaanvragen worden ingediend via het medewerkerportaal.</p>
              </>
            ) : isAllin ? (
              <>
                <p>5.1. De opgebouwde vakantierechten zijn verwerkt in het bruto all-in uurloon.</p>
                <p>5.2. De oproepkracht heeft geen recht op doorbetaald verlof. De vergoeding hiervoor is onderdeel van het all-in uurloon.</p>
              </>
            ) : (
              <>
                <p>5.1. De oproepkracht bouwt {c.vakantie_percentage}% per gewerkt uur vakantie op.</p>
                <p>5.2. De opgebouwde vakantierechten worden bijgehouden en kunnen in overleg worden opgenomen.</p>
                <p>5.3. Vakantieaanvragen worden ingediend via het medewerkerportaal.</p>
              </>
            )}
          </div>

          {/* Art 6 — Ziekte */}
          <div>
            <h3 className="font-bold text-gray-900 mb-1">Artikel 6 — {isVastVerband ? 'Arbeidsongeschiktheid' : 'Ziekte'}</h3>
            {isVastSalaris ? (
              <>
                <p>6.1. Bij arbeidsongeschiktheid wegens ziekte meldt de werknemer zich zo spoedig mogelijk, doch uiterlijk vóór aanvang van de werktijd, ziek bij de werkgever.</p>
                <p>6.2. Bij arbeidsongeschiktheid ontvangt de werknemer gedurende ten hoogste 104 weken loondoorbetaling: 100% van het loon gedurende de eerste 52 weken en 70% gedurende de tweede 52 weken.</p>
                <p>6.3. Bij ziekte gelden de verzuimregels van Cinco Cleaning B.V.</p>
              </>
            ) : (isRegular || isJaaruren) ? (
              <>
                <p>6.1. Bij arbeidsongeschiktheid wegens ziekte meldt de werknemer zich zo spoedig mogelijk, doch uiterlijk vóór aanvang van de werktijd, ziek bij de werkgever.</p>
                <p>6.2. Bij arbeidsongeschiktheid ontvangt de werknemer gedurende ten hoogste 104 weken loondoorbetaling conform de cao Schoonmaak- en Glazenwassersbedrijf: 70% van het loon bij een dienstverband korter dan zes maanden, 90% bij een dienstverband van zes maanden tot twee jaar en 100% bij een dienstverband van twee jaar of langer, met ten minste het wettelijk minimumloon. Bij een bedrijfsongeval bedraagt de loondoorbetaling 100%.</p>
                <p>6.3. Bij ziekte gelden de verzuimregels van Cinco Cleaning B.V.</p>
              </>
            ) : isMinMax ? (
              <>
                <p>6.1. Bij arbeidsongeschiktheid wegens ziekte meldt de werknemer zich zo spoedig mogelijk, doch uiterlijk vóór aanvang van de werktijd, ziek bij de werkgever.</p>
                <p>6.2. Bij arbeidsongeschiktheid ontvangt de werknemer gedurende ten hoogste 104 weken loondoorbetaling conform de cao Schoonmaak- en Glazenwassersbedrijf (70% bij een dienstverband korter dan zes maanden, 90% van zes maanden tot twee jaar, 100% vanaf twee jaar, met ten minste het wettelijk minimumloon), berekend over de garantie-uren en het gemiddelde van de daarboven gewerkte uren in de voorafgaande dertien weken.</p>
                <p>6.3. Bij ziekte gelden de verzuimregels van Cinco Cleaning B.V.</p>
              </>
            ) : (
              <>
                <p>6.1. Bij ziekte meldt de oproepkracht zich zo spoedig mogelijk bij de werkgever.</p>
                <p>6.2. Alleen wanneer de oproepkracht ziek wordt tijdens een oproepperiode of wanneer er een ingeroosterde oproep is, bestaat recht op loondoorbetaling bij ziekte.</p>
                <p>6.3. Bij ziekte gelden de verzuimregels van Cinco Cleaning B.V.</p>
              </>
            )}
          </div>

          {/* Art 7 — Geheimhouding */}
          <div>
            <h3 className="font-bold text-gray-900 mb-1">Artikel 7 — Geheimhouding</h3>
            <p>7.1. De werknemer verplicht zich tot geheimhouding van alle vertrouwelijke informatie die hem in het kader van de arbeidsovereenkomst ter kennis komt.</p>
            <p>7.2. Deze verplichting geldt zowel tijdens als na afloop van het dienstverband.</p>
          </div>

          {/* Art 8 — Nevenwerkzaamheden */}
          <div>
            <h3 className="font-bold text-gray-900 mb-1">Artikel 8 — Nevenwerkzaamheden</h3>
            {isVastSalaris ? (
              <>
                <p>8.1. Het is werkgever bekend dat werknemer naast dit dienstverband als zelfstandige werkzaamheden verricht. Werkgever stemt daarmee in, onder de voorwaarden in dit artikel.</p>
                <p>8.2. De nevenwerkzaamheden mogen niet concurreren met of schade toebrengen aan de activiteiten van werkgever of aan haar merken, en worden niet verricht voor klanten, partners of leveranciers van werkgever zonder voorafgaand overleg.</p>
                <p>8.3. De nevenwerkzaamheden worden verricht buiten de werktijd bij werkgever en zonder gebruik van bedrijfsmiddelen, materialen of bedrijfsinformatie van werkgever.</p>
                <p>8.4. Werknemer richt de omvang van de nevenwerkzaamheden zo in dat de totale arbeidstijd binnen de Arbeidstijdenwet blijft en dat rust, gezondheid en een goede vervulling van de functie bij werkgever niet in het gedrang komen. Bij arbeidsongeschiktheid worden geen nevenwerkzaamheden verricht.</p>
                <p>8.5. Partijen bespreken de nevenwerkzaamheden opnieuw wanneer de omvang of de aard ervan wezenlijk verandert.</p>
              </>
            ) : (
              <p>8.1. Het is de werknemer niet toegestaan zonder voorafgaande schriftelijke toestemming van de werkgever nevenwerkzaamheden te verrichten die de belangen van de werkgever kunnen schaden.</p>
            )}
          </div>

          {/* Art 9+ — depends on type */}
          {isVastVerband ? (
            <>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">Artikel 9 — Opzegtermijn</h3>
                <p>9.1. De opzegtermijn voor de werknemer bedraagt één kalendermaand.</p>
                <p>9.2. De opzegtermijn voor de werkgever is overeenkomstig de wettelijke bepalingen.</p>
                <p>9.3. Opzegging dient schriftelijk te geschieden tegen het einde van een kalendermaand.</p>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">Artikel 10 — Pensioen</h3>
                <p>10.1. De werknemer wordt aangemeld bij het Bedrijfstakpensioenfonds Schoonmaak- en Glazenwassersbedrijf (BPF Schoonmaak) conform het geldende pensioenreglement.</p>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">Artikel 11 — {isVastSalaris ? 'Toepasselijk recht' : 'Toepasselijk recht en cao'}</h3>
                <p>11.1. Op deze arbeidsovereenkomst is het Nederlands recht van toepassing.</p>
                {isVastSalaris ? (
                  <p>11.2. In gevallen waarin deze overeenkomst niet voorziet, gelden de wettelijke bepalingen.</p>
                ) : (
                  <>
                    <p>11.2. Op deze arbeidsovereenkomst is de cao Schoonmaak- en Glazenwassersbedrijf van toepassing.</p>
                    <p>11.3. In gevallen waarin deze overeenkomst niet voorziet, gelden de cao en de wettelijke bepalingen.</p>
                  </>
                )}
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">Artikel 12 — Slotbepalingen</h3>
                <p>12.1. Wijzigingen en aanvullingen op deze overeenkomst zijn slechts geldig indien schriftelijk overeengekomen.</p>
                <p>12.2. Indien enige bepaling van deze overeenkomst nietig of vernietigbaar blijkt, tast dit de geldigheid van de overige bepalingen niet aan.</p>
              </div>
            </>
          ) : (
            <div>
              <h3 className="font-bold text-gray-900 mb-1">Artikel 9 — Toepasselijk recht en slotbepalingen</h3>
              <p>9.1. Op deze arbeidsovereenkomst is het Nederlands recht van toepassing.</p>
              <p>9.2. In gevallen waarin deze overeenkomst niet voorziet, gelden de wettelijke bepalingen.</p>
              <p>9.3. Wijzigingen en aanvullingen op deze overeenkomst zijn slechts geldig indien schriftelijk overeengekomen.</p>
            </div>
          )}
        </div>

        {/* Signature block */}
        {isStilzwijgend ? (
          <p className="mt-12 text-xs text-gray-500">
            Geen handtekeningen: de werknemer werkte na de einddatum van {vorigNummer} door zonder aanzegging, waardoor de
            arbeidsovereenkomst van rechtswege is voortgezet. Deze rij legt die voortzetting vast.
          </p>
        ) : (
        <div className="mt-12 grid grid-cols-2 gap-8">
          <div>
            <p className="text-xs text-gray-500 mb-2">Opgemaakt te Havelte, {formatDateNL(c.datum_opgemaakt)}</p>
            {c.employer_signature_data ? (
              <>
                <p className="text-xs text-gray-500 mb-1">Ondertekend: {c.employer_signed_at ? new Date(c.employer_signed_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}</p>
                <img src={c.employer_signature_data} alt="Werkgever handtekening" className="h-12 object-contain mb-1" />
              </>
            ) : (
              <div className="mt-6 border-b border-gray-900 mb-1"></div>
            )}
            <p className="text-xs text-gray-500">Werkgever: Cinco Cleaning B.V.</p>
            <p className="text-xs text-gray-500">{c.employer_signed_by || c.bedrijf_vertegenwoordigd_door}</p>
          </div>
          <div>
            {c.signed_at ? (
              <>
                <p className="text-xs text-gray-500 mb-2">Ondertekend: {new Date(c.signed_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                {c.signature_data && (
                  <img src={c.signature_data} alt="Handtekening" className="h-12 object-contain mb-1" />
                )}
              </>
            ) : (
              <div className="mt-8 border-b border-gray-900 mb-1"></div>
            )}
            <p className="text-xs text-gray-500">Werknemer:</p>
            <p className="text-xs text-gray-500">{c.voorletters} {c.achternaam}</p>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
